import { labels } from '@/app/constants/labels';
import { isTrainsProduct } from '@/app/lib/utils';
import ChevronRight from '@ixigo/icons/ChevronRight';
import Button from '@ixigo/iui/Button';
import { useCallback, useMemo, useState } from 'react';
import { Fragment } from 'react/jsx-runtime';
import { useShallow } from 'zustand/react/shallow';
import type { NetBankingSection } from '../../domain/payment';
import useSelectedPaymentOption from '../../hooks/useSelectedPaymentOption';
import usePaymentStore from '../../store';
import PaymentSectionCard from '../PaymentSectionCard';
import PaymentsOptionsList from '../PaymentsOptionsList';
import AllBanks from './AllBanks';
import NetbankingBankingOptions from './NetBankingOptions';

const POPULAR_BANKS_COUNT = 5;

interface NetbankingSectionProps {
	// Section data
	section: NetBankingSection;
	showUPISection: boolean;

	// UI state
	isExpanded: boolean;
	showAllBanks: boolean;

	// Handlers
	onShowAllBanks: () => void;
	onCloseAllBanks: () => void;
}

const NetbankingSectionContainer = ({ isExpanded }: { isExpanded: boolean }) => {
	const { netbankingSection, productType } = usePaymentStore(
		useShallow((state) => ({
			netbankingSection: state.netbankingSection!,
			productType: state.transaction!.productType,
		})),
	);

	const { showAllBanks, setShowAllBanks, onClose, showUPISection } = useNetbankingSection(
		netbankingSection,
		productType,
	);

	if (!netbankingSection.options.length) {
		return null;
	}

	return (
		<NetbankingSection
			section={netbankingSection}
			isExpanded={isExpanded}
			showAllBanks={showAllBanks}
			onShowAllBanks={() => setShowAllBanks(true)}
			onCloseAllBanks={onClose}
			showUPISection={showUPISection}
		/>
	);
};

const NetbankingSection = ({
	section,
	isExpanded,
	showAllBanks,
	onShowAllBanks,
	onCloseAllBanks,
	showUPISection,
}: NetbankingSectionProps) => {
	const { logo, heading, subText, options, pgFeeText, tag } = section;

	const majorBanks = useMemo(() => {
		return options.slice(0, POPULAR_BANKS_COUNT);
	}, [options]);

	const hasMoreBanks = useMemo(() => {
		return options.length > POPULAR_BANKS_COUNT;
	}, [options]);

	const { setSelectedPaymentOption } = useSelectedPaymentOption(section.options[0]);

	return (
		<Fragment>
			{isExpanded ? (
				<PaymentsOptionsList
					heading={heading}
					listContent={
						<div className="mx-20">
							<NetbankingBankingOptions
								showUPISection={showUPISection}
								options={majorBanks}
								showPgFeeText={isExpanded}
							/>
							{hasMoreBanks && (
								<Button
									onClick={onShowAllBanks}
									endAdornment={<ChevronRight />}
									variant="flat"
									size="lg"
									className="w-full justify-between"
								>
									{labels.VIEW_ALL_BANKS_TEXT}
								</Button>
							)}
						</div>
					}
				/>
			) : (
				<PaymentSectionCard
					onCardClick={onShowAllBanks}
					logo={logo}
					heading={heading}
					subText={subText}
					tag={tag}
				/>
			)}
			<AllBanks
				showUPISection={showUPISection}
				heading={heading}
				options={options}
				open={showAllBanks}
				onClose={onCloseAllBanks}
				subText={pgFeeText}
				selectDefaultOptionCallback={useCallback(() => {
					setSelectedPaymentOption(section.options[0]);
				}, [section.options, setSelectedPaymentOption])}
			/>
		</Fragment>
	);
};

interface NetbankingSectionState {
	section: NetBankingSection;
	showAllBanks: boolean;
	setShowAllBanks: (show: boolean) => void;
	onClose: () => void;
	showUPISection: boolean;
}

function useNetbankingSection(
	section: NetBankingSection,
	productType: string,
): NetbankingSectionState {
	const [showAllBanks, setShowAllBanks] = useState(false);
	const showUPISection = useMemo(() => !isTrainsProduct(productType), [productType]);

	return {
		section,
		showAllBanks,
		setShowAllBanks,
		onClose: () => setShowAllBanks(false),
		showUPISection,
	};
}

export default NetbankingSectionContainer;
